import { html } from '../lib/html.js';
import { alnumText, isValidPlate, isValidRenavam, maskPlate, normalizePlate, normalizeRenavam, onlyDigits } from '../lib/masks.js';
import { applyApiFieldErrors, autoClearErrors, busy, setFieldError, toastError, toastSuccess, valueOf } from '../lib/ui.js';
import { CATEGORY, CATEGORY_KEYS, scanVehicleDocument, vehicles } from '../services/vehicles.js';
import { Page, go } from './base.js';
import { catIcon, plateBadge, serialBadge } from './vehicle-hero.js';
import { bindUpload, uploadBox } from './vehicle-upload.js';

const THIS_YEAR = new Date().getFullYear();
const upper = (v) => String(v ?? '').toLocaleUpperCase('pt-BR');
/** Número de série / chassi: só letras e números, em maiúsculas (5 a 30). */
const normalizeSerial = (v) => upper(v).replace(/[^A-Z0-9]/g, '').slice(0, 30);

/**
 * Adicionar veículo, em três passos:
 *   1. tipo: carro/moto/utilitário (LIGHT) · caminhão/ônibus (HEAVY) · máquina agrícola (AGRO)
 *   2. dados: placa + RENAVAM (LIGHT/HEAVY) ou número de série (AGRO; placa e RENAVAM opcionais)
 *   3. documento: CRLV-e (ou nota fiscal, para máquina), enviado direto para o armazenamento privado
 * Placa, RENAVAM e série só identificam o veículo: o acesso vem depois da análise do documento.
 * ?tipo=HEAVY abre direto no passo 2.
 */
class VehicleAddPage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/garagem" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Adicionar veículo</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding tc-page" fullscreen>
        <div class="narrow body"></div>
      </ion-content>`;
  }

  setup() {
    this.$('.body').addEventListener('click', (e) => {
      const t = e.target.closest('[data-type]');
      if (t) {
        this.category = t.dataset.type;
        this.suggestions = null;
        this.scanFile = null;
        this.scanId = null;
        return this.vioEnabled && this.category === 'LIGHT' ? this.renderDocumentScan() : this.renderForm();
      }
      if (e.target.closest('[data-action="retry-config"]')) return this.onEnter();
      if (e.target.closest('[data-action="change-document"]')) { this.scanId = null; this.claim = null; this.suggestions = null; return this.renderDocumentScan(); }
      if (e.target.closest('[data-action="back-type"]')) { this.scanId = null; this.suggestions = null; return this.renderType(); }
      if (e.target.closest('[data-action="scan-document"]')) return this.scanDocument();
      if (e.target.closest('[data-action="toggle-plate"]')) {
        this.$('.agro-plate').hidden = !this.$('.agro-plate').hidden;
        e.target.closest('[data-action]').setAttribute('aria-expanded', String(!this.$('.agro-plate').hidden));
      }
    });
  }

  async onEnter() {
    this.scanEpoch = (this.scanEpoch ?? 0) + 1;
    this.claim = null;
    this.suggestions = null;
    this.scanFile = null;
    this.scanId = null;
    const q = new URLSearchParams(location.search).get('tipo');
    this.category = CATEGORY[q] ? q : null;
    this.$('.body').innerHTML = '<p class="hint" role="status">Carregando cadastro…</p>';
    try {
      const config = await vehicles.registrationConfig();
      this.vioEnabled = config.vioEnabled === true;
      if (this.vioEnabled && this.category === 'LIGHT') this.renderDocumentScan();
      else if (this.category) this.renderForm();
      else this.renderType();
    } catch (error) {
      this.$('.body').innerHTML = html`<p role="alert">${error.message || 'Não foi possível carregar o cadastro.'}</p><ion-button data-action="retry-config">Tentar novamente</ion-button>`.value;
    }
  }

  steps(n) {
    return html`<div class="step-bar" aria-label="Passo ${n} de 3">${[1, 2, 3].map((i) => html`<i class="${i <= n ? 'on' : ''}"></i>`)}</div><p class="step">Passo ${n} de 3</p>`;
  }

  renderDocumentScan() {
    this.scanEpoch = (this.scanEpoch ?? 0) + 1;
    this.$('.body').innerHTML = html`
      ${this.steps(2)}
      <button type="button" class="panel row tappable" data-action="back-type" style="margin:4px 0 16px">
        ${catIcon(this.category)}
        <span class="row-main"><span class="row-title">Carro ou moto</span><span class="row-sub">Trocar tipo</span></span>
        <ion-icon class="chevron" name="chevron-forward" aria-hidden="true"></ion-icon>
      </button>
      <h1 class="step-title">Envie o documento do veículo</h1>
      <p class="step-lead">Os dados do veículo serão lidos pela Vio. Depois, você poderá escolher um apelido e confirmar.</p>
      <input class="document-scan-file" type="file" accept="application/pdf,image/jpeg,image/png" hidden />
      <ion-button expand="block" class="scan-pick"><ion-icon slot="start" name="document-attach-outline"></ion-icon>Escolher PDF ou imagem</ion-button>
      <p class="hint small ion-text-center scan-hint">PDF, JPG ou PNG de até 10 MB</p>
` .value;
    this.$('.scan-pick').addEventListener('click', () => this.$('.document-scan-file').click());
    this.$('.document-scan-file').addEventListener('change', () => this.scanDocument());
  }

  async scanDocument() {
    const file = this.$('.document-scan-file')?.files?.[0];
    if (!file) return;
    const epoch = this.scanEpoch;
    const button = this.$('.scan-pick');
    button.disabled = true;
    this.$('.scan-hint').textContent = 'Enviando e lendo o documento…';
    try {
      const result = await scanVehicleDocument(file);
      if (epoch !== this.scanEpoch) return;
      this.scanFile = file;
      this.scanId = result.scanId;
      this.suggestions = result.suggestions ?? {};
      this.renderForm();
      toastSuccess('Documento lido. Escolha um apelido e confirme.');
    } catch (error) {
      if (epoch !== this.scanEpoch) return;
      if (error.code === 'VEHICLE_FLOW_CHANGED') { toastError(error.message); return this.onEnter(); }
      button.disabled = false;
      this.$('.scan-hint').textContent = error.message || 'Não foi possível ler o documento. Tente outro arquivo.';
    }
  }

  renderType() {
    this.scanEpoch = (this.scanEpoch ?? 0) + 1;
    this.$('.body').innerHTML = html`
      ${this.steps(1)}
      <h1 class="step-title">Que tipo de veículo?</h1>
      <p class="step-lead">Assim mostramos os serviços e os lembretes certos para ele.</p>
      <div class="type-opts">
        ${CATEGORY_KEYS.map((k) => {
          const c = CATEGORY[k];
          return html`
            <button type="button" class="type-opt" data-type="${k}">
              ${catIcon(k, 'lg')}
              <span class="row-main">
                <span class="t">${c.name}</span>
                <span class="s">${k === 'LIGHT' ? c.examples : `${c.label}: ${c.examples.charAt(0).toLowerCase()}${c.examples.slice(1)}`}</span>
                <span class="tags">${c.tags.map((t) => html`<span class="tag">${t}</span>`)}</span>
              </span>
              <ion-icon class="chevron" name="chevron-forward" aria-hidden="true"></ion-icon>
            </button>`;
        })}
      </div>`.value;
  }

  renderForm() {
    const fromVio = Boolean(this.scanId);
    const c = CATEGORY[this.category];
    const agro = this.category === 'AGRO';
    const plateFields = html`
      <div class="row">
        <ion-input name="plate" value="${this.suggestions?.plate ?? ''}" label="Placa${agro ? ' (opcional)' : ''}" label-placement="floating" fill="outline" maxlength="12" autocapitalize="characters"></ion-input>
        <ion-input name="renavam" value="${this.suggestions?.renavam ?? ''}" label="RENAVAM${agro ? ' (opcional)' : ''}" label-placement="floating" fill="outline" inputmode="numeric" maxlength="20"></ion-input>
      </div>`;
    this.$('.body').innerHTML = html`
      ${this.steps(fromVio ? 3 : 2)}
      <button type="button" class="panel row tappable" data-action="back-type" style="margin:4px 0 16px">
        ${catIcon(this.category)}
        <span class="row-main"><span class="row-title">${c.name}</span><span class="row-sub">${c.label} · trocar</span></span>
        <ion-icon class="chevron" name="chevron-forward" aria-hidden="true"></ion-icon>
      </button>
      <form novalidate>
        <ion-list lines="none" class="form">
          ${agro
            ? html`
              <ion-input name="serialNumber" value="${this.suggestions?.serialNumber ?? ''}" label="Número de série ou chassi" label-placement="floating" fill="outline" maxlength="40" autocapitalize="characters" helper-text="Fica na plaqueta de identificação da máquina e na nota fiscal"></ion-input>
              <button type="button" class="link-btn" data-action="toggle-plate" aria-expanded="false" style="align-self:flex-start">É emplacada? Informar placa e RENAVAM</button>
              <div class="agro-plate" hidden>${plateFields}</div>`
            : html`${plateFields}<ion-input name="serialNumber" value="${this.suggestions?.serialNumber ?? ''}" label="Chassi (opcional)" label-placement="floating" fill="outline" maxlength="40" autocapitalize="characters"></ion-input>`}
          <ion-input name="nickname" value="${this.suggestions?.nickname ?? ''}" autocapitalize="characters" label="Apelido (opcional)" label-placement="floating" fill="outline" maxlength="40" helper-text="${agro ? 'Ex.: Trator do sítio' : this.category === 'HEAVY' ? 'Ex.: Caminhão 03' : 'Ex.: Carro da família'}"></ion-input>
          ${this.suggestions && Object.keys(this.suggestions).length ? html`<p class="hint small">Dados fornecidos pela Vio. Apenas o apelido pode ser alterado.</p>` : html`<p class="hint small">Se souber, adiantamos marca, modelo e ano. A equipe confere tudo pelo documento.</p>`}
          <div class="row">
            <ion-input name="brand" value="${this.suggestions?.brand ?? ''}" autocapitalize="characters" label="Marca" label-placement="floating" fill="outline" maxlength="40"></ion-input>
            <ion-input name="model" value="${this.suggestions?.model ?? ''}" autocapitalize="characters" label="Modelo" label-placement="floating" fill="outline" maxlength="80"></ion-input>
          </div>
          <div class="row">
            <ion-input name="modelYear" value="${this.suggestions?.modelYear ?? ''}" label="Ano modelo" label-placement="floating" fill="outline" inputmode="numeric" maxlength="4"></ion-input>
            ${agro ? '' : html`<ion-input name="color" value="${this.suggestions?.color ?? ''}" autocapitalize="characters" label="Cor" label-placement="floating" fill="outline" maxlength="30"></ion-input>`}
          </div>
        </ion-list>
        <ion-button type="submit" expand="block" class="submit">${fromVio ? 'Confirmar cadastro' : 'Continuar'}</ion-button>
      </form>
      ${fromVio ? html`<p class="hint small">Se o CPF do documento for o mesmo da sua conta, a aprovação será automática. Se for diferente ou o veículo tiver outro dono no TicketCar, o documento seguirá para análise.</p><ion-button expand="block" fill="clear" data-action="change-document">Enviar outro documento</ion-button>` : ''}
      <p class="hint small ion-text-center">${agro ? 'Sem placa? Sem problema: a máquina é identificada pelo número de série.' : 'Placa e RENAVAM estão no CRLV-e. O RENAVAM tem 11 dígitos.'}</p>`.value;

    const f = this.$('form');
    if (fromVio) for (const field of f.querySelectorAll('ion-input:not([name="nickname"])')) field.readonly = true;
    autoClearErrors(f);
    const input = (n) => f.querySelector(`ion-input[name="${n}"]`);
    input('plate').addEventListener('ionInput', (e) => (e.target.value = maskPlate(e.target.value)));
    input('renavam').addEventListener('ionInput', (e) => (e.target.value = onlyDigits(e.target.value).slice(0, 11)));
    input('modelYear').addEventListener('ionInput', (e) => (e.target.value = onlyDigits(e.target.value).slice(0, 4)));
    input('serialNumber')?.addEventListener('ionInput', (e) => (e.target.value = normalizeSerial(e.target.value)));
    // marca, modelo, cor e apelido: MAIÚSCULAS, como no documento, só letras, números e espaço simples
    for (const n of ['nickname', 'brand', 'model', 'color']) input(n)?.addEventListener('ionInput', (e) => (e.target.value = upper(alnumText(e.target.value, { typing: true }))));
    f.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit(f);
    });
  }

  async confirmScanned(f) {
    await busy(f.querySelector('.submit'), async () => {
      try {
        const nickname = upper(alnumText(valueOf(f, 'nickname')));
        const r = await vehicles.createClaim({ scanId: this.scanId, ...(nickname ? { nickname } : {}) });
        this.claim = r.claim;
        const result = await vehicles.submitClaim(r.claim.id, this.scanFile?.name?.slice(0, 120));
        toastSuccess(result.claim.status === 'APPROVED' ? 'Documento aprovado automaticamente! Veículo adicionado.' : 'Documento enviado para análise. Avisaremos quando ela terminar.');
        go(`/vehicles/${r.vehicle.id}`, 'root');
      } catch (error) {
        toastError(error.message);
        if (['VEHICLE_FLOW_CHANGED', 'VIO_SCAN_REQUIRED'].includes(error.code)) await this.onEnter();
        else if (['ALREADY_HAS_ACCESS', 'CLAIM_EXISTS'].includes(error.code)) go('/garagem', 'root');
      }
    });
  }

  async submit(f) {
    if (this.scanId) return this.confirmScanned(f);
    const agro = this.category === 'AGRO';
    const val = (n) => valueOf(f, n);
    const err = (n, m) => setFieldError(f.querySelector(`ion-input[name="${n}"]`), m);
    const data = {
      category: this.category,
      plate: normalizePlate(val('plate')),
      renavam: normalizeRenavam(val('renavam')),
      serialNumber: normalizeSerial(val('serialNumber')),
      nickname: upper(alnumText(val('nickname'))),
      brand: upper(alnumText(val('brand'))),
      model: upper(alnumText(val('model'))),
      modelYear: val('modelYear') ? Number(val('modelYear')) : undefined,
      color: upper(alnumText(val('color'))),
    };
    let ok = true;
    if (agro) {
      if (data.serialNumber.length < 5) (ok = false), err('serialNumber', 'Informe o número de série (pelo menos 5 letras ou números)');
      if (data.plate && !isValidPlate(data.plate)) (ok = false), err('plate', 'Placa inválida');
      if (data.renavam && !isValidRenavam(data.renavam)) (ok = false), err('renavam', 'RENAVAM inválido');
      if (!ok && (data.plate || data.renavam)) this.$('.agro-plate').hidden = false;
    } else {
      if (!isValidPlate(data.plate)) (ok = false), err('plate', 'Placa inválida');
      if (!isValidRenavam(data.renavam)) (ok = false), err('renavam', 'RENAVAM inválido');
      if (data.serialNumber && data.serialNumber.length < 5) (ok = false), err('serialNumber', 'Chassi inválido');
    }
    if (data.modelYear && (data.modelYear < 1950 || data.modelYear > THIS_YEAR + 1)) (ok = false), err('modelYear', 'Ano inválido');
    if (!ok) return;
    for (const k of Object.keys(data)) if (data[k] === '' || data[k] === undefined) delete data[k];

    await busy(f.querySelector('.submit'), async () => {
      try {
        const r = await vehicles.createClaim(data);
        this.claim = r.claim;
        this.alreadyOwned = Boolean(r.alreadyOwned);
        this.renderUpload(r.vehicle);
      } catch (e) {
        if (e.code === 'VIO_SCAN_REQUIRED' || e.code === 'VEHICLE_FLOW_CHANGED') { toastError(e.message); return this.onEnter(); }
        if (e.code === 'PLATE_RENAVAM_MISMATCH') return err(agro && !data.renavam ? 'plate' : 'renavam', e.message);
        if (e.code === 'SERIAL_MISMATCH') return err('serialNumber', e.message);
        if (e.code === 'ALREADY_HAS_ACCESS' || e.code === 'CLAIM_EXISTS') {
          toastError(e.message);
          return go('/garagem', 'root');
        }
        if (!applyApiFieldErrors(f, e.details)) toastError(e.message);
      }
    });
  }

  renderUpload(vehicle) {
    const agro = (vehicle.category ?? this.category) === 'AGRO';
    this.$('.body').innerHTML = html`
      ${this.steps(3)}
      <div class="panel row" style="margin:4px 0 16px">
        ${catIcon(vehicle.category ?? this.category)}
        <span class="row-main">
          <span class="row-title">${[vehicle.brand, vehicle.model].filter(Boolean).join(' ') || CATEGORY[vehicle.category ?? this.category].name}</span>
          <span class="row-sub">${vehicle.renavam ? `RENAVAM ${vehicle.renavam}` : 'Identificada pelo número de série'}</span>
        </span>
        ${vehicle.plateFormatted ? plateBadge(vehicle.plateFormatted) : vehicle.serialNumber ? serialBadge(vehicle.serialNumber) : ''}
      </div>
      ${this.alreadyOwned
        ? html`
          <div class="panel notice">
            <span class="row-icon warning"><ion-icon name="information-circle-outline"></ion-icon></span>
            <div>
              <strong>Este veículo já está no TicketCar com outro dono</strong>
              <p>Se você comprou, envie ${agro ? 'o documento' : 'o CRLV-e'} no seu nome. O dono anterior será avisado, e o histórico de manutenções vem junto com o veículo.</p>
              <p>Ainda não transferiu? Peça ao vendedor para tocar em "Vendi este veículo" no app dele: o veículo passa para você na hora.</p>
            </div>
          </div>`
        : ''}
      ${uploadBox({ category: vehicle.category ?? this.category })}
      <ion-button expand="block" fill="clear" class="later">Enviar depois</ion-button>`.value;
    this.$('.later').addEventListener('click', () => go(`/vehicles/${vehicle.id}`, 'root'));
    bindUpload(this, this.claim.id, () => {
      toastSuccess('Documento enviado! Avisaremos quando a análise terminar.');
      go(`/vehicles/${vehicle.id}`, 'root');
    });
  }
}

customElements.define('page-vehicle-add', VehicleAddPage);
